import type paper from "paper";
import type { Cutout, Decoration, Piece, StitchLine, Vec2 } from "./Project";
import { pieceStitchLines } from "./Project";
import { translatePathData } from "./constructionTranslate";
export { translatePathData } from "./constructionTranslate";
import {
  mirrorSeamHandles,
  reverseSeamHandles,
  rotateSeamHandles,
  type SeamHandle,
} from "./seamGeometry";
import { flipPathData, type FlipAxis } from "./constructionFlip";
import { clearPieceRounding } from "./pieceRounding";
import { axisAngleDeg } from "./mirrorAxis";
import type { AxisSpec } from "./constructionMirror";
import {
  composeRotations,
  normalizeRotation,
  rotatePathData,
} from "./constructionRotation";
import {
  boxToBoxTransformWithFlip,
  resizePathDataByBox,
  type Box,
} from "./constructionResize";

/** The persisted baked-rotation metadata a leather object carries (#971) —
 *  same construction-line semantics: geometry is stored rotated, these recover
 *  the un-rotated frame. */
type RotationMeta = { rotation?: number; rotationCenter?: Vec2 };

/** Compose a NEW rotation (`deg` about `(cx,cy)`) onto an object's existing
 *  baked-rotation metadata, mirroring `rotateConstructionLine`'s tracking: an
 *  object already tilted about a pivot composes to a single equivalent
 *  rotation; a fresh rotation records `deg` about the pivot; a composition that
 *  collapses to a pure translation clears the tilt. */
function trackObjectRotation(
  prev: RotationMeta,
  deg: number,
  cx: number,
  cy: number,
): RotationMeta {
  const oldDeg = prev.rotation ?? 0;
  const oldCenter = prev.rotationCenter;
  if (oldCenter && Math.abs(oldDeg) > 1e-6) {
    const composed = composeRotations(oldDeg, oldCenter, deg, { x: cx, y: cy });
    return composed
      ? { rotation: composed.deg, rotationCenter: composed.center }
      : {};
  }
  return { rotation: normalizeRotation(deg), rotationCenter: { x: cx, y: cy } };
}

/**
 * Move (or clear) a user-parked rotation pivot (#990 follow-up).
 *
 * `pivotPoint` is where the user dragged the crosshair, so it has to travel
 * with the geometry — a translate or a rotate that left it behind would make
 * the next spin orbit a stale point. A resize maps it through the same
 * transform as the geometry, so it keeps its place relative to the object
 * (#1111). `map` null CLEARS it.
 */
/** Shift a parked pivot rigidly with a translated object — used by the
 *  move-one-child paths (a cutout / seam dragged on its own), which don't go
 *  through `translatePiece`. */
export function translatePivotPoint<T extends { pivotPoint?: Vec2 }>(
  obj: T,
  dx: number,
  dy: number,
): T {
  return withPivotPoint(obj, (pv) => ({ x: pv.x + dx, y: pv.y + dy }));
}

function withPivotPoint<T extends { pivotPoint?: Vec2 }>(
  obj: T,
  map: ((p: Vec2) => Vec2) | null,
): T {
  if (obj.pivotPoint === undefined) return obj;
  const next = { ...obj };
  if (map) next.pivotPoint = map(obj.pivotPoint);
  else delete next.pivotPoint;
  return next;
}

/** Return `obj` with its rotation metadata set to `meta` (or cleared when
 *  `meta` is empty — a composition that collapsed to a translation). */
function withRotationMeta<T extends RotationMeta>(
  obj: T,
  meta: RotationMeta,
): T {
  const next = { ...obj };
  if (meta.rotation !== undefined && meta.rotationCenter) {
    next.rotation = meta.rotation;
    next.rotationCenter = meta.rotationCenter;
  } else {
    delete next.rotation;
    delete next.rotationCenter;
  }
  return next;
}

/** Map a point through the un-rotated resize frame: undo the object's baked
 *  tilt about `center`, apply the box-to-box affine `t`, re-apply the tilt.
 *  Matches how `resizePathDataByBox` transforms path strings under `rot`, so
 *  points (holes, seam vertices) and paths stay in lockstep. When the object
 *  has no tilt (`rot` null) this is just the affine. */
function mapPointResized(
  pt: Vec2,
  t: { sx: number; sy: number; tx: number; ty: number },
  rot: { deg: number; center: Vec2 } | null,
): Vec2 {
  let p = pt;
  if (rot) p = rotatePointClosedForm(p, -rot.deg, rot.center);
  p = { x: t.sx * p.x + t.tx, y: t.sy * p.y + t.ty };
  if (rot) p = rotatePointClosedForm(p, rot.deg, rot.center);
  return p;
}

/** The resize transform applied to a RELATIVE vector — a seam's bezier handle
 *  (#982). Same rotate-out / scale / rotate-back as `mapPointResized`, minus the
 *  translation: a handle is an offset from its anchor, and translating it would
 *  drag the curve away from the point it belongs to. */
function mapHandleResized(
  v: Vec2,
  t: { sx: number; sy: number },
  rot: { deg: number; center: Vec2 } | null,
): Vec2 {
  const ORIGIN = { x: 0, y: 0 };
  let p = v;
  if (rot) p = rotatePointClosedForm(p, -rot.deg, ORIGIN);
  p = { x: t.sx * p.x, y: t.sy * p.y };
  if (rot) p = rotatePointClosedForm(p, rot.deg, ORIGIN);
  return p;
}

/** Map a whole handle list through a resize. */
function mapHandlesResized(
  handles: SeamHandle[] | undefined,
  t: { sx: number; sy: number },
  rot: { deg: number; center: Vec2 } | null,
): SeamHandle[] | undefined {
  if (!handles) return undefined;
  return handles.map((h) => ({
    in: mapHandleResized(h.in, t, rot),
    out: mapHandleResized(h.out, t, rot),
  }));
}

/** Rotate a point by `deg` about `(center)` in closed form (y-down, degrees). */
function rotatePointClosedForm(pt: Vec2, deg: number, center: Vec2): Vec2 {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const dx = pt.x - center.x;
  const dy = pt.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/**
 * Pure geometry translation for a leather piece and its sub-features. Extracted
 * from the Select tool so it can be reused both for moving a part (drag commit)
 * and for baking a linked part's cut-layout transform into the exporters, which
 * walk model geometry directly rather than the Paper scene (see
 * `applyPieceLayout`).
 */

/** Translate one decoration by `(dx, dy)`, dispatching on its kind. */
export function translateDecoration(
  dec: Decoration,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Decoration {
  switch (dec.kind) {
    case "stamp":
      return {
        ...dec,
        position: { x: dec.position.x + dx, y: dec.position.y + dy },
      };
    case "lineStamp":
      return {
        ...dec,
        pathData: translatePathData(dec.pathData, dx, dy, scope),
      };
    case "fill":
      return {
        ...dec,
        regionPathData: translatePathData(dec.regionPathData, dx, dy, scope),
      };
    case "offset":
      return {
        ...dec,
        sourcePathData: translatePathData(dec.sourcePathData, dx, dy, scope),
      };
    case "freePath":
      return {
        ...dec,
        pathData: translatePathData(dec.pathData, dx, dy, scope),
      };
  }
}

/** Translate a whole piece — shape, holes, cutouts, stitch lines, decorations —
 *  by `(dx, dy)`. Returns a new piece; the input is untouched. */
/**
 * Carry an outline's ROUNDING SOURCE through the same transform as its visible
 * geometry (#1072).
 *
 * `cornerRounding.sharpPathData` is the un-rounded source, and the commit
 * pipeline re-derives the visible outline from it on every commit
 * (`deriveRoundedLeatherCorners`). So a transform that moves the drawn path and
 * leaves the source behind is undone the instant the pipeline runs: the part
 * snapped back to where it started while its cutouts and seams — translated
 * directly, with no derive step — stayed where they were dropped.
 *
 * Only for RIGID transforms (translate / rotate / mirror), where the radii
 * still describe the moved shape exactly. A resize is a reshape, and hands the
 * geometry over instead — see `transformPieceByAffine`.
 */
function mapRoundingSource<
  T extends { cornerRounding?: Piece["cornerRounding"] },
>(obj: T, mapPath: (d: string) => string): T {
  const rounding = obj.cornerRounding;
  if (!rounding) return obj;
  return {
    ...obj,
    cornerRounding: {
      ...rounding,
      sharpPathData: mapPath(rounding.sharpPathData),
    },
  };
}

export function translatePiece(
  piece: Piece,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): Piece {
  // A baked rotation's pivot moves rigidly with the geometry, or a later
  // rotate would orbit a stale point (#971).
  const movePivot = <T extends { pivotPoint?: Vec2 }>(o: T): T =>
    withPivotPoint(o, (pv) => ({ x: pv.x + dx, y: pv.y + dy }));
  const shift = (m: RotationMeta): RotationMeta =>
    m.rotationCenter
      ? {
          rotation: m.rotation,
          rotationCenter: {
            x: m.rotationCenter.x + dx,
            y: m.rotationCenter.y + dy,
          },
        }
      : m;
  const movePath = (d: string) => translatePathData(d, dx, dy, scope);
  const movePt = (pt: Vec2): Vec2 => ({ x: pt.x + dx, y: pt.y + dy });
  return withRotationMeta(
    movePivot(
      mapRoundingSource(
        {
          ...piece,
          shapePathData: movePath(piece.shapePathData),
          ...mappedTrace(piece, movePt),
          holes: piece.holes.map((h) => ({
            ...h,
            position: { x: h.position.x + dx, y: h.position.y + dy },
          })),
          cutouts: piece.cutouts.map((c) =>
            withRotationMeta(
              movePivot(
                mapRoundingSource(
                  {
                    ...c,
                    pathData: movePath(c.pathData),
                    ...mappedTrace(c, movePt),
                  },
                  movePath,
                ),
              ),
              shift(c),
            ),
          ),
          stitchLines: pieceStitchLines(piece).map((s) =>
            withRotationMeta(
              movePivot({
                ...s,
                points: s.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })),
                // Keep the full authored seam (#780) in sync so a later independent
                // stitch move re-crops from the correct geometry.
                authoredPoints: s.authoredPoints?.map((pt) => ({
                  x: pt.x + dx,
                  y: pt.y + dy,
                })),
                ...mappedTrace(s, movePt),
              }),
              shift(s),
            ),
          ),
          decorations: piece.decorations.map((d) =>
            translateDecoration(d, dx, dy, scope),
          ),
        },
        movePath,
      ),
    ),
    shift(piece),
  );
}

// --- Mirror / rotate (#795) -------------------------------------------------
//
// Flip / rotate a whole part in place, structured exactly like `translatePiece`:
// transform `shapePathData` plus every sub-feature so the part and its stitch
// lines / holes / cutouts / decorations move together. Path strings reuse the
// construction helpers (`flipPathData` / `rotatePathData`, Paper CompoundPath
// transforms); plain points reflect in closed form (matching `scale(±1, center)`)
// or rotate via Paper so they stay consistent with the transformed path.

/** Reflect a point across the pivot's vertical (`horizontal` flip) or
 *  horizontal (`vertical` flip) axis — the closed form of `scale(-1,1,center)`
 *  / `scale(1,-1,center)`. */
function reflectPoint(pt: Vec2, axis: FlipAxis, cx: number, cy: number): Vec2 {
  return axis === "horizontal"
    ? { x: 2 * cx - pt.x, y: pt.y }
    : { x: pt.x, y: 2 * cy - pt.y };
}

/** Rotate a point about the pivot by `deg`, using Paper so it matches exactly
 *  how `rotatePathData` transforms the path strings. */
function rotatePoint(
  pt: Vec2,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Vec2 {
  const p = new scope.Point(pt.x, pt.y).rotate(deg, new scope.Point(cx, cy));
  return { x: p.x, y: p.y };
}

/** Mirror one decoration about the pivot, dispatching on its kind. Stamps/fills
 *  also negate their tracked `rotation` (a reflection reverses spin). */
export function flipDecoration(
  dec: Decoration,
  axis: FlipAxis,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Decoration {
  switch (dec.kind) {
    case "stamp":
      return {
        ...dec,
        position: reflectPoint(dec.position, axis, cx, cy),
        rotation: -dec.rotation,
      };
    case "lineStamp":
      return {
        ...dec,
        pathData: flipPathData(dec.pathData, axis, cx, cy, scope),
      };
    case "fill":
      return {
        ...dec,
        regionPathData: flipPathData(dec.regionPathData, axis, cx, cy, scope),
        rotation: -dec.rotation,
      };
    case "offset":
      return {
        ...dec,
        sourcePathData: flipPathData(dec.sourcePathData, axis, cx, cy, scope),
      };
    case "freePath":
      return {
        ...dec,
        pathData: flipPathData(dec.pathData, axis, cx, cy, scope),
      };
  }
}

/** Rotate one decoration about the pivot by `deg`, dispatching on its kind.
 *  Stamps/fills also add `deg` to their tracked `rotation`. */
export function rotateDecoration(
  dec: Decoration,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Decoration {
  switch (dec.kind) {
    case "stamp":
      return {
        ...dec,
        position: rotatePoint(dec.position, deg, cx, cy, scope),
        rotation: dec.rotation + deg,
      };
    case "lineStamp":
      return {
        ...dec,
        pathData: rotatePathData(dec.pathData, cx, cy, deg, scope),
      };
    case "fill":
      return {
        ...dec,
        regionPathData: rotatePathData(dec.regionPathData, cx, cy, deg, scope),
        rotation: dec.rotation + deg,
      };
    case "offset":
      return {
        ...dec,
        sourcePathData: rotatePathData(dec.sourcePathData, cx, cy, deg, scope),
      };
    case "freePath":
      return {
        ...dec,
        pathData: rotatePathData(dec.pathData, cx, cy, deg, scope),
      };
  }
}

/** Mirror a whole piece about a pivot — shape, holes, cutouts, stitch lines,
 *  decorations. `slantAngle` is measured from the local stitch direction, and a
 *  reflection reverses handedness, so it flips sign (round/diamond have
 *  slantAngle 0 and are unaffected). A reflection also reverses the seam's
 *  winding, so its stored `direction` (#796) toggles cw↔ccw (#787). Returns a
 *  new piece; input untouched. */
export function mirrorPiece(
  piece: Piece,
  axis: FlipAxis,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Piece {
  const flipPath = (d: string) => flipPathData(d, axis, cx, cy, scope);
  const flipPt = (pt: Vec2): Vec2 => reflectPoint(pt, axis, cx, cy);
  return withRotationMeta(
    mapRoundingSource(
      {
        ...piece,
        shapePathData: flipPath(piece.shapePathData),
        ...mappedTrace(piece, flipPt),
        holes: piece.holes.map((h) => ({
          ...h,
          position: reflectPoint(h.position, axis, cx, cy),
        })),
        cutouts: piece.cutouts.map((c) => mirrorCutout(c, axis, cx, cy, scope)),
        stitchLines: pieceStitchLines(piece).map((s) =>
          mirrorStitchLine(s, axis, cx, cy),
        ),
        decorations: piece.decorations.map((d) =>
          flipDecoration(d, axis, cx, cy, scope),
        ),
      },
      flipPath,
    ),
    reflectRotationMeta(piece, axis, cx, cy),
  );
}

/** Mirror a single cutout's outline about the pivot, baking the geometry and
 *  reflecting its tilt metadata (#971). Extracted from `mirrorPiece` so the
 *  leather Mirror sub-tool (#973) can mirror one cutout on its own. */
export function mirrorCutout(
  cutout: Cutout,
  axis: FlipAxis,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Cutout {
  const flipPath = (d: string) => flipPathData(d, axis, cx, cy, scope);
  return withRotationMeta(
    mapRoundingSource(
      {
        ...cutout,
        pathData: flipPath(cutout.pathData),
        ...mappedTrace(cutout, (pt) => reflectPoint(pt, axis, cx, cy)),
      },
      flipPath,
    ),
    reflectRotationMeta(cutout, axis, cx, cy),
  );
}

/** Mirror a single stitch seam about the pivot. A reflection reverses the
 *  seam's winding, so its stored `direction` (#796) toggles cw↔ccw (#787). To
 *  keep the DRAWN row identical to a pure reflection — same holes, same
 *  working-area start/end, same slant — every vertex is reflected AND the order
 *  reversed: `directedStitchLine` re-reverses a ccw seam at placement time, so
 *  the two reversals cancel and only the honest label + winding change.
 *  (Mirroring twice returns the original point order and direction.) Extracted
 *  from `mirrorPiece` so the leather Mirror sub-tool (#973) can mirror one seam
 *  on its own. */
export function mirrorStitchLine(
  line: StitchLine,
  axis: FlipAxis,
  cx: number,
  cy: number,
): StitchLine {
  return withRotationMeta(
    {
      ...line,
      points: line.points.map((pt) => reflectPoint(pt, axis, cx, cy)).reverse(),
      // Handles get BOTH halves of what happened to the points (#982): the
      // reflection, and the reversal — order plus in/out swap. Reflecting
      // without reversing leaves every curve bulging the wrong way.
      handles: reverseSeamHandles(mirrorSeamHandles(line.handles, axis)),
      // Keep the full authored seam (#780) in the same reversed+reflected order
      // so a later independent re-crop walks it the same way.
      authoredPoints: line.authoredPoints
        ?.map((pt) => reflectPoint(pt, axis, cx, cy))
        .reverse(),
      // The slant, from the now-reflected local direction, flips sign.
      slantAngle: -line.slantAngle,
      direction: (line.direction ?? "cw") === "ccw" ? "cw" : "ccw",
      // Reversed as well as reflected, so the derive re-walks the trace the
      // same way the reflected `points` above are walked.
      ...mappedTrace(line, (pt) => reflectPoint(pt, axis, cx, cy), true),
    },
    reflectRotationMeta(line, axis, cx, cy),
  );
}

// --- Arbitrary-axis mirror (#973) -------------------------------------------
//
// The flips above reflect across the pivot's horizontal or vertical axis, which
// is all the Flip commands ever needed. The leather Mirror sub-tool picks its
// axis off an outline — any angle — so it needs the general reflection.
//
// Rather than a second set of per-feature mirror functions (the controller fork
// #990 warns about), the general case is EXPRESSED with the axis-aligned ones:
// rotate the object so the axis lies horizontal, flip across it, rotate back.
// Every sub-feature rule — the seam's slant sign and cw↔ccw toggle, reflected
// tilt metadata, decoration handling — is inherited rather than restated.

/** How close to axis-aligned (in degrees) still takes the exact single-flip
 *  path instead of the rotate → flip → rotate composition. */
const AXIS_ALIGN_EPS_DEG = 1e-6;

/** Reflect one object across an arbitrary `axis`, given its own axis-aligned
 *  `rotate` and `flip`. Axis-aligned axes short-circuit to a single flip: it's
 *  the common case (mirroring across a vertical centre line), it avoids three
 *  path round-trips, and it leaves no zero-angle tilt metadata on an object
 *  that had none. */
function acrossAxis<T>(
  obj: T,
  axis: AxisSpec,
  rotate: (o: T, deg: number, cx: number, cy: number) => T,
  flip: (o: T, fa: FlipAxis, cx: number, cy: number) => T,
): T {
  const ox = axis.origin.x;
  const oy = axis.origin.y;
  // A point axis is a central inversion — exactly a 180° turn about the pivot.
  if (axis.kind === "point" || !axis.dir) return rotate(obj, 180, ox, oy);
  const theta = axisAngleDeg(axis);
  const mod = ((theta % 180) + 180) % 180;
  // "vertical" flip mirrors across the pivot's HORIZONTAL line, and vice versa.
  if (mod < AXIS_ALIGN_EPS_DEG || 180 - mod < AXIS_ALIGN_EPS_DEG) {
    return flip(obj, "vertical", ox, oy);
  }
  if (Math.abs(mod - 90) < AXIS_ALIGN_EPS_DEG) {
    return flip(obj, "horizontal", ox, oy);
  }
  return rotate(
    flip(rotate(obj, -theta, ox, oy), "vertical", ox, oy),
    theta,
    ox,
    oy,
  );
}

/** Mirror a whole piece — shape, holes, cutouts, seams, decorations — across an
 *  arbitrary axis line or pivot point. */
export function mirrorPieceAcross(
  piece: Piece,
  axis: AxisSpec,
  scope: paper.PaperScope,
): Piece {
  return acrossAxis(
    piece,
    axis,
    (o, deg, cx, cy) => rotatePiece(o, deg, cx, cy, scope),
    (o, fa, cx, cy) => mirrorPiece(o, fa, cx, cy, scope),
  );
}

/** Mirror a single cutout across an arbitrary axis line or pivot point. */
export function mirrorCutoutAcross(
  cutout: Cutout,
  axis: AxisSpec,
  scope: paper.PaperScope,
): Cutout {
  return acrossAxis(
    cutout,
    axis,
    (o, deg, cx, cy) => rotateCutout(o, deg, cx, cy, scope),
    (o, fa, cx, cy) => mirrorCutout(o, fa, cx, cy, scope),
  );
}

/** Mirror a single stitch seam across an arbitrary axis line or pivot point. */
export function mirrorStitchLineAcross(
  line: StitchLine,
  axis: AxisSpec,
  scope: paper.PaperScope,
): StitchLine {
  return acrossAxis(
    line,
    axis,
    (o, deg, cx, cy) => rotateStitchLine(o, deg, cx, cy, scope),
    (o, fa, cx, cy) => mirrorStitchLine(o, fa, cx, cy),
  );
}

/** Reflect an object's baked-rotation metadata across a mirror axis: a
 *  reflection reverses spin (negate the angle) and moves the pivot to its
 *  mirror image. Empty when the object carries no tilt. */
function reflectRotationMeta(
  obj: RotationMeta,
  axis: FlipAxis,
  cx: number,
  cy: number,
): RotationMeta {
  if (obj.rotation === undefined || !obj.rotationCenter) return {};
  return {
    rotation: normalizeRotation(-obj.rotation),
    rotationCenter: reflectPoint(obj.rotationCenter, axis, cx, cy),
  };
}

/** Rotate a whole piece about a pivot by `deg` — shape, holes, cutouts, stitch
 *  lines, decorations. `slantAngle` is relative to the local stitch direction,
 *  which rotates with the points, so it needs no adjustment. Returns a new
 *  piece; input untouched. */
export function rotatePiece(
  piece: Piece,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Piece {
  const spinPivot = <T extends { pivotPoint?: Vec2 }>(o: T): T =>
    withPivotPoint(o, (pv) => rotatePoint(pv, deg, cx, cy, scope));
  const spinPath = (d: string) => rotatePathData(d, cx, cy, deg, scope);
  const spinPt = (pt: Vec2): Vec2 => rotatePoint(pt, deg, cx, cy, scope);
  const rotated: Piece = spinPivot(
    mapRoundingSource(
      {
        ...piece,
        shapePathData: spinPath(piece.shapePathData),
        ...mappedTrace(piece, spinPt),
        holes: piece.holes.map((h) => ({
          ...h,
          position: rotatePoint(h.position, deg, cx, cy, scope),
        })),
        // Sub-features rotate WITH the part (same pivot) — bake their geometry and
        // compose their OWN tilt too, so a later independent cutout / seam resize
        // still un-skews correctly (#971).
        cutouts: piece.cutouts.map((c) =>
          withRotationMeta(
            mapRoundingSource(
              {
                ...c,
                pathData: spinPath(c.pathData),
                ...mappedTrace(c, spinPt),
              },
              spinPath,
            ),
            trackObjectRotation(c, deg, cx, cy),
          ),
        ),
        stitchLines: pieceStitchLines(piece).map((s) =>
          withRotationMeta(
            {
              ...s,
              points: s.points.map((pt) => rotatePoint(pt, deg, cx, cy, scope)),
              handles: rotateSeamHandles(s.handles, deg),
              authoredPoints: s.authoredPoints?.map((pt) =>
                rotatePoint(pt, deg, cx, cy, scope),
              ),
              ...mappedTrace(s, spinPt),
            },
            trackObjectRotation(s, deg, cx, cy),
          ),
        ),
        decorations: piece.decorations.map((d) =>
          rotateDecoration(d, deg, cx, cy, scope),
        ),
      },
      spinPath,
    ),
  );
  return withRotationMeta(rotated, trackObjectRotation(piece, deg, cx, cy));
}

// --- Box-to-box resize (#971) -----------------------------------------------
//
// On-canvas drag-resize + the numeric W/H panel both scale a part / cutout /
// seam by mapping its OLD bounding box onto a NEW one (with optional axis
// flips when a corner is dragged past its anchor). Path strings reuse the
// shared `resizePathDataByBox`; plain points map through the same box-to-box
// affine so they stay consistent with the transformed path. Scalar
// properties (hole diameter, stitch pitch/size, decoration tool scalars) stay
// fixed — resizing changes geometry, not the maker's tooling spec (#971).

/** Reposition / reshape one decoration for a box-to-box resize — its GEOMETRY
 *  moves (`mapPos` for a stamp anchor, `scalePath` for the vector kinds), but
 *  its "tooling" scalars (`scale` motif size, `spacing`, `distance`,
 *  `strokeWidth`) are KEPT: resizing a part changes where decorations sit, not
 *  the stamp/tool settings the maker chose (#971, same principle as stitch
 *  pitch). */
function scaleDecorationByBox(
  dec: Decoration,
  mapPos: (p: Vec2) => Vec2,
  scalePath: (d: string) => string,
): Decoration {
  switch (dec.kind) {
    case "stamp":
      return { ...dec, position: mapPos(dec.position) };
    case "lineStamp":
      return { ...dec, pathData: scalePath(dec.pathData) };
    case "fill":
      return { ...dec, regionPathData: scalePath(dec.regionPathData) };
    case "offset":
      return { ...dec, sourcePathData: scalePath(dec.sourcePathData) };
    case "freePath":
      return { ...dec, pathData: scalePath(dec.pathData) };
  }
}

/** Resize a whole piece by mapping `orig`→`next` (with `flip`) — shape, holes,
 *  cutouts, stitch lines, decorations. Shared by the numeric W/H field (top-left
 *  anchored, no flip) and the on-canvas resize drag (#971). A seam's pitch/size
 *  scale with the part (whole-part resize keeps its long-standing behaviour;
 *  resizing a seam ON ITS OWN keeps pitch — see `resizeStitchLineByBox`). */
export function transformPieceByAffine(
  piece: Piece,
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  scope: paper.PaperScope,
): Piece {
  const t = boxToBoxTransformWithFlip(orig, next, flip);
  // `orig`/`next` are in the part's UN-ROTATED frame, so scale runs there:
  // un-rotate by the baked tilt, apply the box affine, re-rotate — no skew
  // (#971 Design A). A moved pivot maps through the same transform.
  const rot = rotationOf(piece);
  const mapPos = (p: Vec2) => mapPointResized(p, t, rot);
  const scalePath = (d: string) =>
    resizePathDataByBox(d, orig, next, flip, scope, pathRot(rot));
  // A parked pivot travels with the geometry, here and on every child, so it
  // keeps its place relative to the object (#1111) — as the live preview
  // showed it during the drag.
  const mapPivot = <T extends { pivotPoint?: Vec2 }>(o: T): T =>
    withPivotPoint(o, mapPos);
  // Corner rounding hands its geometry over for the same reason (#1072): the
  // radii were measured against the OLD size, and a non-uniform scale cannot
  // keep a circular arc circular anyway. The already-rounded outline becomes
  // the source — the same handoff a vertex drag makes. Carrying the metadata
  // instead would re-derive the old size straight back over the resize.
  const bakeRounding = <T extends { cornerRounding?: Piece["cornerRounding"] }>(
    o: T,
  ): T => clearPieceRounding(o);
  return bakeRounding(
    mapPivot({
      ...piece,
      shapePathData: scalePath(piece.shapePathData),
      ...mappedTrace(piece, mapPos),
      // Geometry scales; the hole DIAMETER is a hardware spec, not geometry —
      // keep it (#971).
      holes: piece.holes.map((h) => ({ ...h, position: mapPos(h.position) })),
      cutouts: piece.cutouts.map((c) =>
        bakeRounding(
          mapPivot({
            ...c,
            pathData: scalePath(c.pathData),
            ...mappedTrace(c, mapPos),
            ...mappedCenter(c, mapPos),
          }),
        ),
      ),
      stitchLines: pieceStitchLines(piece).map((s) =>
        mapPivot({
          ...s,
          points: s.points.map(mapPos),
          handles: mapHandlesResized(s.handles, t, rot),
          authoredPoints: s.authoredPoints?.map(mapPos),
          ...mappedTrace(s, mapPos),
          // pitch / size are the maker's stitching spec — geometry scales (holes
          // re-lay along the new length), the spec stays (#971).
          ...mappedCenter(s, mapPos),
        }),
      ),
      decorations: piece.decorations.map((d) =>
        scaleDecorationByBox(d, mapPos, scalePath),
      ),
      ...mappedCenter(piece, mapPos),
    }),
  );
}

/** The baked-rotation frame of an object, or null when it carries no tilt. */
function rotationOf(obj: RotationMeta): { deg: number; center: Vec2 } | null {
  return obj.rotation !== undefined && obj.rotationCenter
    ? { deg: obj.rotation, center: obj.rotationCenter }
    : null;
}

/** Adapt a rotation frame for `resizePathDataByBox`'s `rot` param. */
function pathRot(
  rot: { deg: number; center: Vec2 } | null,
): { deg: number; pivot: Vec2 } | undefined {
  return rot ? { deg: rot.deg, pivot: rot.center } : undefined;
}

/** A partial `{ rotationCenter }` with the pivot mapped through a resize, or
 *  `{}` when the object has none — spread into the resized object so a later
 *  rotate still orbits the visual centre. The angle is unchanged. */
/** Freehand leather keeps its trace in `rawPoints` and re-derives its visible
 *  geometry from it on every commit (`deriveLeatherSmoothing`), so a transform
 *  that moves the geometry but leaves the trace behind is undone by the very
 *  next derive — the shape springs straight back to where it was drawn
 *  (#1110). Every transform in this file carries the trace alongside the
 *  geometry, which is what construction lines have done since #171.
 *
 *  Spread into the object literal like {@link mappedCenter}, and empty for the
 *  ordinary non-freehand object so nothing else changes. `reverse` is for the
 *  seam mirror, whose reflection also reverses the point order.
 *
 *  `smoothness` is deliberately NOT scaled. It is a simplify tolerance rather
 *  than geometry, and construction leaves it alone through a resize too — so
 *  the slider keeps the meaning the maker set it to. */
function mappedTrace<T extends { rawPoints?: Vec2[] }>(
  obj: T,
  map: (p: Vec2) => Vec2,
  reverse = false,
): { rawPoints?: Vec2[] } {
  if (!obj.rawPoints) return {};
  const pts = obj.rawPoints.map(map);
  return { rawPoints: reverse ? pts.reverse() : pts };
}

function mappedCenter(
  obj: RotationMeta,
  mapPos: (p: Vec2) => Vec2,
): { rotationCenter?: Vec2 } {
  return obj.rotationCenter
    ? { rotationCenter: mapPos(obj.rotationCenter) }
    : {};
}

/** Rotate a single cutout's outline about the pivot by `deg`, baking the
 *  geometry and composing its tilt metadata (#971). */
export function rotateCutout(
  cutout: Cutout,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Cutout {
  return withRotationMeta(
    withPivotPoint(
      {
        ...cutout,
        pathData: rotatePathData(cutout.pathData, cx, cy, deg, scope),
        ...mappedTrace(cutout, (pt) => rotatePoint(pt, deg, cx, cy, scope)),
      },
      (pv) => rotatePoint(pv, deg, cx, cy, scope),
    ),
    trackObjectRotation(cutout, deg, cx, cy),
  );
}

/** Resize a single cutout by mapping `orig`→`next` in its UN-ROTATED frame
 *  (#971) — `orig`/`next` come from the cutout's un-rotated bounds, so the
 *  baked tilt is undone before scaling and re-applied after; the pivot maps
 *  through. */
export function resizeCutoutByBox(
  cutout: Cutout,
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  scope: paper.PaperScope,
): Cutout {
  const rot = rotationOf(cutout);
  const mapPos = (p: Vec2) =>
    mapPointResized(p, boxToBoxTransformWithFlip(orig, next, flip), rot);
  return withPivotPoint(
    {
      ...cutout,
      pathData: resizePathDataByBox(
        cutout.pathData,
        orig,
        next,
        flip,
        scope,
        pathRot(rot),
      ),
      ...mappedTrace(cutout, mapPos),
      ...mappedCenter(cutout, mapPos),
    },
    // A parked pivot travels with the geometry (#1111).
    mapPos,
  );
}

/** Rotate a single stitch seam about the pivot by `deg`, baking the points and
 *  composing its tilt metadata (#971). `slantAngle` is measured from the local
 *  seam direction (which rotates with the points), so it needs no adjustment.
 *  Holes re-derive from the new points on commit. */
export function rotateStitchLine(
  line: StitchLine,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): StitchLine {
  return withRotationMeta(
    withPivotPoint(
      {
        ...line,
        points: line.points.map((p) => rotatePoint(p, deg, cx, cy, scope)),
        // Relative handles need the angle only — no centre (#982).
        handles: rotateSeamHandles(line.handles, deg),
        authoredPoints: line.authoredPoints?.map((p) =>
          rotatePoint(p, deg, cx, cy, scope),
        ),
        ...mappedTrace(line, (p) => rotatePoint(p, deg, cx, cy, scope)),
      },
      (pv) => rotatePoint(pv, deg, cx, cy, scope),
    ),
    trackObjectRotation(line, deg, cx, cy),
  );
}

/** Resize a single stitch seam by mapping `orig`→`next` in its UN-ROTATED frame
 *  (#971). The controller aspect-locks a seam resize (a slanted/diamond hole
 *  footprint can't be non-uniformly scaled), so this is a uniform scale of the
 *  point geometry. The seam's `pitch` / `size` / `slantAngle` are KEPT — spacing
 *  stays the maker's chosen value and holes re-lay along the new length.
 *  `authoredPoints` scale in lockstep; the pivot maps through. */
export function resizeStitchLineByBox(
  line: StitchLine,
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  scope: paper.PaperScope,
): StitchLine {
  const t = boxToBoxTransformWithFlip(orig, next, flip);
  const rot = rotationOf(line);
  const mapPos = (p: Vec2) => mapPointResized(p, t, rot);
  // A parked pivot travels with the geometry (#1111).
  return withPivotPoint(
    {
      ...line,
      points: line.points.map(mapPos),
      handles: mapHandlesResized(line.handles, t, rot),
      authoredPoints: line.authoredPoints?.map(mapPos),
      ...mappedTrace(line, mapPos),
      ...mappedCenter(line, mapPos),
    },
    mapPos,
  );
}

/**
 * Bake a linked part's cut-layout transform (#652) into its geometry, returning a
 * translated copy. The transform lives on `constructionOutline.layout` so it
 * survives the construction re-derive (which rewrites `shapePathData`); it is
 * applied at render time (a Paper group transform) and here for the exporters,
 * which read model geometry directly. Translation only for now —
 * `rotation`/`cx`/`cy` are reserved. A no-op for an unlinked piece or a zero
 * layout, so it is safe to call on every piece.
 */
export function applyPieceLayout(piece: Piece, scope: paper.PaperScope): Piece {
  const layout = piece.constructionOutline?.layout;
  if (!layout) return piece;
  const { dx, dy } = layout;
  if (dx === 0 && dy === 0) return piece;
  return translatePiece(piece, dx, dy, scope);
}

/**
 * Accumulate a drag delta into a linked part's `layout` transform (initialising
 * it when absent), leaving `shapePathData` and sub-features untouched. Returns
 * the piece unchanged when it isn't linked — the caller translates geometry
 * directly in that case.
 */
export function addPieceLayoutDelta(
  piece: Piece,
  dx: number,
  dy: number,
): Piece {
  const outline = piece.constructionOutline;
  if (!outline) return piece;
  const cur = outline.layout ?? { dx: 0, dy: 0 };
  return {
    ...piece,
    constructionOutline: {
      ...outline,
      layout: { ...cur, dx: cur.dx + dx, dy: cur.dy + dy },
    },
  };
}

/** True when `piece` is linked and carries a non-zero cut-layout offset —
 *  i.e. it has been moved off its construction (assembled) position (#677). */
export function hasPieceLayoutOffset(piece: Piece): boolean {
  const layout = piece.constructionOutline?.layout;
  return !!layout && (layout.dx !== 0 || layout.dy !== 0);
}

/** The translation between a part's DISPLAYED position and its ORIGINAL
 *  (construction-space) geometry (#786): `displayed = original + offset`.
 *
 *  A linked part's cut-layout translation (`constructionOutline.layout`) is
 *  applied only at render/export time (see `applyPieceLayout`) and is NOT baked
 *  into `shapePathData` / `holes` / `cutouts` / `stitchLines`, so in Spread view
 *  the part is drawn `offset` away from where its model geometry actually lives.
 *  Zero in the Assembled view (the layout is ignored there) and for unlinked
 *  parts (no `layout`). Draw tools use this to (a) hit-test the cursor against a
 *  part's DISPLAYED outline and (b) store created features back in ORIGINAL
 *  space so the render translate doesn't double-offset them. */
export function pieceDisplayOffset(
  piece: Piece,
  partLayoutView: "spread" | "assembled",
): { dx: number; dy: number } {
  if (partLayoutView !== "spread") return { dx: 0, dy: 0 };
  const layout = piece.constructionOutline?.layout;
  if (!layout) return { dx: 0, dy: 0 };
  return { dx: layout.dx, dy: layout.dy };
}

/** Drop a linked part's cut-layout offset, snapping it back to where it was
 *  created from the construction scaffold (#677). The shape stays linked
 *  (`constructionOutline` is kept, only `layout` is cleared); a no-op on an
 *  unlinked part or one with no offset. */
export function clearPieceLayout(piece: Piece): Piece {
  const outline = piece.constructionOutline;
  if (!outline || !outline.layout) return piece;
  const { layout: _drop, ...rest } = outline;
  return { ...piece, constructionOutline: rest };
}
